import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ApiError } from '../../api/client';
import { clientsApi, paymentMethodsApi, paymentsApi } from '../../api/endpoints';
import { useAuth } from '../../app/AuthProvider';
import { useToast } from '../../app/ToastProvider';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { StatusBadge } from '../../components/StatusBadge';
import { formatDate, formatPeso, todayInput } from '../../utils/format';
import type { ClientListItem } from '../../api/types';

interface RecordPaymentDialogProps {
  open: boolean;
  /** Pre-selects a client when opened from a client profile. */
  presetClient?: { id: number; label: string };
  /** Restricts the dialog to collecting and invoicing the reactivation fee. */
  reactivationMode?: boolean;
  onClose: () => void;
}

export function RecordPaymentDialog({ open, presetClient, reactivationMode = false, onClose }: RecordPaymentDialogProps) {
  const { user } = useAuth();
  const canWaiveReactivationFee = user?.role === 'ORG_ADMIN';
  const [clientId, setClientId] = useState<number | null>(null);
  const [clientSearch, setClientSearch] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [paymentDate, setPaymentDate] = useState(todayInput());
  const [amount, setAmount] = useState('');
  const [rebate, setRebate] = useState('0');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [remarks, setRemarks] = useState('');
  const [reactivateSubscription, setReactivateSubscription] = useState(false);
  const [reconnectionFee, setReconnectionFee] = useState('100');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const toast = useToast();
  const queryClient = useQueryClient();
  const debouncedSearch = useDebouncedValue(clientSearch);

  useEffect(() => {
    if (!open) return;
    setClientId(presetClient?.id ?? null);
    setClientSearch(presetClient?.label ?? '');
    setPaymentMethod('CASH');
    setPaymentDate(todayInput());
    setAmount(reactivationMode ? '100' : '');
    setRebate('0');
    setReferenceNumber('');
    setRemarks('');
    setReactivateSubscription(reactivationMode);
    setReconnectionFee('100');
    setFieldErrors({});
    setError(null);
  }, [open, presetClient, reactivationMode]);

  const { data: paymentMethods = [] } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () => paymentMethodsApi.list(),
    enabled: open,
  });

  const { data: clientResults, isFetching: searching } = useQuery({
    queryKey: ['clients', 'picker', debouncedSearch],
    queryFn: () =>
      clientsApi.list({ page: 1, pageSize: 20, search: debouncedSearch || undefined, status: 'ALL' }),
    enabled: open && !presetClient,
  });

  const { data: snapshot, isFetching: loadingSnapshot } = useQuery({
    queryKey: ['payment-snapshot', clientId],
    queryFn: () => paymentsApi.snapshot(clientId as number),
    enabled: open && clientId !== null,
  });

  useEffect(() => {
    if (open && !reactivationMode) {
      setReactivateSubscription(Boolean(snapshot?.suspendedSubscription));
    }
  }, [open, reactivationMode, snapshot?.suspendedSubscription]);

  const mutation = useMutation({
    mutationFn: () =>
      paymentsApi.record({
        clientId: clientId as number,
        paymentMethod,
        paymentDate,
        amountReceived: Number(amount),
        rebate: Number(rebate || 0),
        reactivateSubscription,
        reactivationOnly: reactivationMode,
        reconnectionFee: reactivateSubscription ? Number(reconnectionFee || 0) : 0,
        referenceNumber: referenceNumber.trim() || undefined,
        remarks: remarks.trim() || undefined,
      }),
    onSuccess: async ({ data, message }) => {
      const applied = data.appliedToInvoices
        .map((line) => `${line.invoiceNumber} ${formatPeso(line.appliedAmount)}`)
        .join(', ');
      toast.success(
        data.reactivated && !data.reconnectionInvoiceNumber
          ? message
          : applied
          ? `${message} Applied to ${applied}.`
          : `${message} Held as advance credit of ${formatPeso(data.unappliedCredit)}.`,
      );

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['payments'] }),
        queryClient.invalidateQueries({ queryKey: ['clients'] }),
        queryClient.invalidateQueries({ queryKey: ['client'] }),
        queryClient.invalidateQueries({ queryKey: ['invoices'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
      onClose();
    },
    onError: (caught: unknown) => {
      if (caught instanceof ApiError) {
        setFieldErrors(caught.byField());
        setError(
          caught.fieldErrors.length > 0
            ? caught.fieldErrors.map(({ field, message }) => `${field}: ${message}`).join(' ')
            : caught.message,
        );
      } else {
        setError('The payment could not be recorded.');
      }
    },
  });

  const amountValue = Number(amount);
  const isAdminFeeWaiver = reactivationMode && canWaiveReactivationFee &&
    Number(reconnectionFee) === 0 && amountValue === 0;
  const amountValid = Number.isFinite(amountValue) && (amountValue > 0 || isAdminFeeWaiver);
  const canSubmit = clientId !== null && amountValid && Boolean(paymentDate) &&
    (!reactivationMode ||
      (Boolean(snapshot?.suspendedSubscription) && Number(reconnectionFee) === amountValue));

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    if (!canSubmit) {
      setError(`Select a client and enter ${canWaiveReactivationFee && reactivationMode ? 'a valid amount' : 'an amount greater than zero'}.`);
      return;
    }
    mutation.mutate();
  };

  const options: ClientListItem[] = clientResults?.items ?? [];

  return (
    <Dialog open={open} onClose={mutation.isPending ? undefined : onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle>{reactivationMode ? 'Record Reactivation Payment' : 'Record Payment'}</DialogTitle>
        <DialogContent dividers>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Grid container spacing={2}>
            <Grid size={12}>
              {presetClient ? (
                <TextField label="Client" value={presetClient.label} disabled />
              ) : (
                <Autocomplete
                  options={options}
                  loading={searching}
                  getOptionLabel={(option) => `${option.clientNumber} — ${option.displayName}`}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onInputChange={(_event, value) => setClientSearch(value)}
                  onChange={(_event, value) => setClientId(value?.id ?? null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Client"
                      placeholder="Type a name or client number…"
                      required
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {searching ? <CircularProgress size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                />
              )}
            </Grid>

            {clientId !== null && !reactivationMode ? (
              <Grid size={12}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    bgcolor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {loadingSnapshot || !snapshot ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={16} />
                      <Typography variant="body2">Loading outstanding balance…</Typography>
                    </Stack>
                  ) : (
                    <>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={3}
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Outstanding Balance
                          </Typography>
                          <Typography variant="h6" color="error.main">
                            {formatPeso(snapshot.outstandingBalance)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Advance Credit
                          </Typography>
                          <Typography variant="h6" color="success.main">
                            {formatPeso(snapshot.credit)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Outstanding Invoices
                          </Typography>
                          <Typography variant="h6">{snapshot.outstandingInvoices.length}</Typography>
                        </Box>
                      </Stack>

                      {snapshot.outstandingInvoices.length > 0 ? (
                        <>
                          <Divider sx={{ my: 1.5 }} />
                          <Typography variant="caption" color="text.secondary">
                            The payment is applied to the oldest due invoice first.
                          </Typography>
                          <Table size="small" sx={{ mt: 1 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Invoice</TableCell>
                                <TableCell>Due Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Outstanding</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {snapshot.outstandingInvoices.map((invoice) => (
                                <TableRow key={invoice.id}>
                                  <TableCell>{invoice.invoiceNumber}</TableCell>
                                  <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                                  <TableCell>
                                    <StatusBadge status={invoice.status} />
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatPeso(invoice.outstandingAmount)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </>
                      ) : null}
                    </>
                  )}
                </Box>
              </Grid>
            ) : null}

            {!reactivationMode && snapshot?.suspendedSubscription ? (
              <Grid size={12}>
                <Alert severity="warning">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={reactivateSubscription}
                        onChange={(event) => setReactivateSubscription(event.target.checked)}
                      />
                    }
                    label={`Reactivate ${snapshot.suspendedSubscription.subscriptionNumber} when the invoice balance and fee are fully paid`}
                  />
                  {reactivateSubscription ? (
                    <TextField
                      sx={{ mt: 1 }}
                      label="Reactivation Fee"
                      type="number"
                      value={reconnectionFee}
                      onChange={(event) => setReconnectionFee(event.target.value)}
                      error={Boolean(fieldErrors.reconnectionFee)}
                      helperText={fieldErrors.reconnectionFee ?? (snapshot.suspendedSubscription.branch?.toUpperCase() === 'TUMANA' ? 'Tumana default: PHP 100. This remains manually adjustable.' : 'Set the applicable branch fee manually.')}
                      slotProps={{ htmlInput: { min: 0.01, step: 0.01 } }}
                      fullWidth
                      required
                    />
                  ) : null}
                </Alert>
              </Grid>
            ) : null}
            {reactivationMode && snapshot?.suspendedSubscription ? (
              <Grid size={12}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {canWaiveReactivationFee
                    ? `Enter 0 to reactivate ${snapshot.suspendedSubscription.subscriptionNumber} without a fee. A positive fee will create a reactivation invoice.`
                    : `Payment will create a reactivation-fee invoice for ${snapshot.suspendedSubscription.subscriptionNumber} and reactivate the subscription.`}
                </Alert>
                <TextField
                  label="Reactivation Fee"
                  type="number"
                  value={reconnectionFee}
                  onChange={(event) => {
                    setReconnectionFee(event.target.value);
                    setAmount(event.target.value);
                  }}
                  error={Boolean(fieldErrors.reconnectionFee)}
                  helperText={fieldErrors.reconnectionFee ?? (canWaiveReactivationFee ? 'Administrators may enter PHP 0 to waive the fee.' : snapshot.suspendedSubscription.branch?.toUpperCase() === 'TUMANA' ? 'Tumana default: PHP 100. This remains manually adjustable.' : 'Set the applicable branch fee manually.')}
                  slotProps={{ htmlInput: { min: canWaiveReactivationFee ? 0 : 0.01, step: 0.01 } }}
                  fullWidth
                  required
                />
              </Grid>
            ) : null}
            {!reactivationMode ? (
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Rebate"
                  type="number"
                  value={rebate}
                  onChange={(event) => setRebate(event.target.value)}
                  helperText="Discount applied to the outstanding invoice"
                  slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                />
              </Grid>
            ) : null}
            {snapshot && !reactivationMode ? (
              <Grid size={12}>
                <Alert severity="info">
                  Final cost after rebate: {formatPeso(Math.max(0, snapshot.outstandingBalance + (reactivateSubscription ? Number(reconnectionFee || 0) : 0) - Number(rebate || 0)))}
                </Alert>
              </Grid>
            ) : null}
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                select
                label="Payment Method"
                value={paymentMethod}
                onChange={(event) => setPaymentMethod(event.target.value)}
                required
              >
                {paymentMethods.map((method) => (
                  <MenuItem key={method.id} value={method.code}>
                    {method.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Payment Date"
                type="date"
                value={paymentDate}
                onChange={(event) => setPaymentDate(event.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                error={Boolean(fieldErrors.paymentDate)}
                helperText={fieldErrors.paymentDate}
                required
              />
            </Grid>
            {!reactivationMode ? (
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Amount Received"
                  type="number"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  error={Boolean(fieldErrors.amountReceived)}
                  helperText={fieldErrors.amountReceived ?? 'In Philippine Pesos'}
                  slotProps={{ htmlInput: { min: 0.01, step: 0.01 } }}
                  required
                />
              </Grid>
            ) : null}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Reference Number"
                value={referenceNumber}
                onChange={(event) => setReferenceNumber(event.target.value)}
                helperText="GCash or bank transaction reference, if any."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Remarks"
                value={remarks}
                onChange={(event) => setRemarks(event.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit" disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending || !canSubmit}>
            {mutation.isPending ? 'Saving…' : reactivationMode ? (isAdminFeeWaiver ? 'Reactivate Without Fee' : 'Pay & Reactivate') : 'Save Payment'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

